import { Button } from '@/components/ui/button';
import { MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '@/utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminMessages({ messages, onRefresh }) {
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await adminApi.delete(`/contact/${id}`);
      toast.success('Message deleted successfully!');
      onRefresh();
    } catch {
      toast.error('Failed to delete message');
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-stone-100">
      <div className="flex items-center mb-6">
        <MessageSquare className="w-6 h-6 text-[#E85D75] mr-3" />
        <h2 className="text-2xl font-serif font-medium text-stone-800">Contact Messages</h2>
      </div>

      <div className="space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            data-testid={`message-${message.id}`}
            className="bg-stone-50 p-6 rounded-xl border border-stone-100"
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-stone-800">{message.name}</h3>
                <p className="text-sm text-stone-500">{message.email}</p>
                {message.phone && <p className="text-sm text-stone-500">{message.phone}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-400">
                  {new Date(message.created_at).toLocaleDateString()}
                </span>
                <Button
                  onClick={() => handleDelete(message.id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-message-${message.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-stone-600 mb-4">{message.message}</p>
            {message.kitten_id && (
              <p className="text-sm text-[#E85D75] mb-4">
                Interested in kitten ID: {message.kitten_id}
              </p>
            )}

            {/* Reply Actions */}
            <div className="flex gap-2 pt-4 border-t border-stone-200">
              <Button
                onClick={() => {
                  const subject = encodeURIComponent(`Re: Your inquiry about our kittens`);
                  const body = encodeURIComponent(
                    `Hi ${message.name},\n\nThank you for your interest in our Scottish Straight kittens!\n\n${
                      message.kitten_id ? `Regarding the kitten you inquired about, ` : ''
                    }\n\n[Your response here]\n\nBest regards,\nKittenState\n929-333-1936\nsales@kittenstate.com`
                  );
                  window.location.href = `mailto:${message.email}?subject=${subject}&body=${body}`;
                }}
                className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
                data-testid={`reply-message-${message.id}`}
              >
                📧 Reply via Email
              </Button>

              {message.phone && (
                <a href={`tel:${message.phone}`}>
                  <Button variant="outline" className="rounded-full">
                    📞 Call {message.phone}
                  </Button>
                </a>
              )}

              <Button
                onClick={() => {
                  navigator.clipboard.writeText(message.email);
                  toast.success('Email copied to clipboard!');
                }}
                variant="outline"
                className="rounded-full"
              >
                📋 Copy Email
              </Button>
            </div>
          </div>
        ))}
      </div>
      {messages.length === 0 && (
        <div className="text-center py-12 text-stone-500">No messages received yet.</div>
      )}
    </div>
  );
}
